import React, { ReactElement, useState } from "react";
import { alpha, Box, Button, Stack, SxProps, Theme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { CustomTextView } from "../../component/CustomTextView";
import {
  COLOR_BLACK,
  COLOR_LESS_BLACK,
  COLOR_MAIN,
  COLOR_WHITE,
} from "../../../data/colors/Colors";

export interface DrawerItem {
  isRoute: boolean;
  title: string;
  icon: ReactElement;
  onClick?: () => void;
  isSelected?: boolean;
  href?: string;
  textColor?: string;
  bgColor?: string;
  isTab?: boolean;
  onCloseDrawer?: () => void;
}

export function DrawerItemView(props: DrawerItem) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const isRtl = i18n.dir() === "rtl";
  const { isSelected, isTab, isRoute, href, onClick, onCloseDrawer } = props;

  // --- Logic Handlers ---
  const handleOnClick = () => {
    if (isRoute && href) {
      if (location.pathname === href) return;
      window.scrollTo(0, 0);
      navigate(href);

      setTimeout(() => {
        onCloseDrawer?.();
      }, 300);
    } else {
      onClick?.();
    }
  };

  // --- Dynamic Styles ---
  const buttonSx: SxProps<Theme> = {
    transition: "all 0.3s ease-in-out",
    background: isRoute
      ? isSelected
        ? COLOR_WHITE
        : alpha(COLOR_WHITE, 0.1)
      : props.bgColor,
    px: isTab ? 2 : 4,
    py: isTab ? 1 : 2,
    mt: isTab ? 0 : 1,
    ml: isTab ? 0 : 2,
    mr: isTab ? 0 : 2,
    borderRadius: 32,
    border: isRoute ? undefined : "1px solid white",
    "&:hover": {
      backgroundColor: isRoute
        ? isSelected
          ? COLOR_WHITE
          : alpha(COLOR_WHITE, 0.3)
        : alpha(COLOR_BLACK, 0.4),
      boxShadow: 1,
    },
    "& .MuiTouchRipple-child": { backgroundColor: COLOR_LESS_BLACK },
  };

  return (
    <Stack position="relative">
      <Button sx={buttonSx} onClick={handleOnClick} LinkComponent="a">
        <Stack direction="row" alignItems="center" flex={1}>
          {React.Children.map(props.icon, (child) => {
            return React.cloneElement(child as ReactElement<any>, {
              sx: {
                color: isRoute
                  ? !isSelected
                    ? alpha(COLOR_WHITE, 0.4)
                    : COLOR_MAIN
                  : COLOR_WHITE,
              },
            });
          })}

          <Box width={12} />

          <CustomTextView
            text={props.title}
            textColor={
              isSelected ? COLOR_MAIN : (props.textColor ?? alpha(COLOR_WHITE, 0.4))
            }
            props={{ textTransform: "none" }}
            fontWeight={isSelected ? "bold" : "normal"}
          />
        </Stack>
      </Button>
    </Stack>
  );
}
